import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { supabase } from "@/integrations/supabase/client";
import { User } from "lucide-react";

type Patient = {
  id: string;
  full_name: string;
  avatar_url: string | null;
};

export function RecentPatients() {
  const navigate = useNavigate();
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadRecentPatients();
  }, []);

  const loadRecentPatients = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Usar edge function para buscar pacientes por última atividade
      const { data, error } = await supabase.functions.invoke('get-recent-patients-with-activity');

      if (error) {
        console.error("Error loading recent patients:", error);
        // Fallback para query simples se edge function falhar
        const { data: fallbackData, error: fallbackError } = await supabase
          .from("patients_onboarding")
          .select("id, full_name, avatar_url")
          .eq("owner_id", user.id)
          .order("updated_at", { ascending: false })
          .limit(5);
        
        if (!fallbackError && fallbackData) {
          setPatients(fallbackData);
        }
      } else {
        setPatients(data || []);
      }
    } catch (error) {
      console.error("Error loading recent patients:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-muted-foreground">Pacientes Recentes</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-4 flex flex-col items-center gap-2">
                <div className="w-12 h-12 rounded-full bg-muted" />
                <div className="h-3 w-20 bg-muted rounded" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold text-muted-foreground">Pacientes Recentes</h3>
      {patients.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8">
          Nenhum paciente cadastrado ainda
        </p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {patients.map((patient) => (
            <Card
              key={patient.id}
              onClick={() => navigate(`/app/pacientes/${patient.id}`)}
              className="cursor-pointer hover:border-primary/30 hover:shadow-md transition-all"
            >
              <CardContent className="p-4 flex flex-col items-center text-center gap-2">
                <Avatar className="h-12 w-12 border-2 border-primary/20">
                  <AvatarImage src={patient.avatar_url || undefined} />
                  <AvatarFallback className="bg-primary/10 text-primary">
                    <User className="h-6 w-6" />
                  </AvatarFallback>
                </Avatar>
                <p className="text-xs font-medium truncate w-full">
                  {patient.full_name}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
